import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { UNDER_13_CONSENT_QUESTIONS } from './signup/consentQuestions';
import { saveConsentSettings, notifyParentOnChildToggle } from './signup/ConsentService';

export default function AccountConsentSettings({ className = '' }) {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const user = auth.currentUser;

  useEffect(() => {
    let isMounted = true;
    async function load() {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const ref = doc(db, 'consentSettings', user.uid);
        const snap = await getDoc(ref);
        if (isMounted) {
          if (snap.exists()) {
            const existing = snap.data();
            // Load saved settings as-is, don't auto-reset
            // User's saved preferences should be respected
            console.log('📥 Loaded consent settings from Firestore:', existing);
            setSettings(existing);
          } else {
            // Auto-create defaults for users without consent settings yet
            // Logic:
            // - If under-13 (student with parentEmail), use checkout-provided consent if available
            // - Else (over-13 student or parent/ngo_admin), default all toggles to ON
            let accountTypeFromProfile = 'student';
            let parentEmailFromProfile = null;
            let consentFromCheckout = null;
            try {
              const profileSnap = await getDoc(doc(db, 'users', user.uid));
              if (profileSnap.exists()) {
                const p = profileSnap.data();
                accountTypeFromProfile = p?.accountType || 'student';
                parentEmailFromProfile = p?.parentEmail || null;
                consentFromCheckout = p?.consentFromCheckout || null;
              }
            } catch (_) {}

            const isUnder13Profile = accountTypeFromProfile === 'student' && !!parentEmailFromProfile;

            let defaultConsent = {};
            if (isUnder13Profile && consentFromCheckout && typeof consentFromCheckout === 'object') {
              defaultConsent = UNDER_13_CONSENT_QUESTIONS.reduce((acc, q) => {
                acc[q.id] = !!consentFromCheckout[q.id];
                return acc;
              }, {});
            } else {
              // Over-13 and parent/ngo_admin default to ON
              defaultConsent = UNDER_13_CONSENT_QUESTIONS.reduce((acc, q) => {
                acc[q.id] = true;
                return acc;
              }, {});
            }

            const defaultSettings = {
              userId: user.uid,
              childUserId: user.uid,
              accountType: accountTypeFromProfile,
              parentEmail: parentEmailFromProfile,
              consent: defaultConsent,
            };
            try {
              await saveConsentSettings(defaultSettings);
              setSettings(defaultSettings);
            } catch (_) {
              setSettings(defaultSettings);
            }
          }
        }
      } catch (e) {
        if (isMounted) setError(e?.message || 'Failed to load consent settings');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    load();
    return () => { isMounted = false; };
  }, [user]);

  const isUnder13 = useMemo(() => {
    if (!settings) return false;
    return settings.accountType === 'student' && !!settings.parentEmail;
  }, [settings]);

  const consent = settings?.consent || {};

  const onParentToggle = (key, value) => {
    if (!user) return;
    
    // Store changes in pendingChanges state (don't save yet)
    setPendingChanges(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Update local state for immediate UI feedback
    const updated = { ...(settings?.consent || {}), [key]: value };
    setSettings(prev => ({ ...(prev || {}), consent: updated }));
    setSaveSuccess(false); // Clear success message when new changes are made
  };

  const handleSaveSettings = async () => {
    if (!user || !settings) return;
    
    setSaving(true);
    setError('');
    setSaveSuccess(false);
    
    try {
      // Merge pending changes with existing consent settings
      const updatedConsent = {
        ...(settings.consent || {}),
        ...pendingChanges
      };
      
      // Check if user is under 13 and trying to enable artwork sharing
      if (isUnder13 && pendingChanges['artwork_public_allowed'] === true) {
        if (settings?.parentEmail) {
          try {
            // Send parent consent email
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/consent/send-parent-email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                parentEmail: settings.parentEmail,
                childUserId: user.uid,
                toggleName: 'artwork_public_allowed',
                desiredValue: true
              }),
            });
            
            if (response.ok) {
              alert('An email has been sent to your parent for approval. The setting will be enabled once they approve.');
              // Don't save artwork sharing yet - wait for parent approval
              // Remove it from the consent update
              delete updatedConsent['artwork_public_allowed'];
            } else {
              alert('Failed to send email to parent. Please try again.');
              setSaving(false);
              return;
            }
          } catch (error) {
            console.error('Error sending parent email:', error);
            alert('Failed to send email to parent. Please try again.');
            setSaving(false);
            return;
          }
        }
      }
      
      // Save all consent settings
      console.log('💾 Saving consent settings:', {
        userId: user.uid,
        accountType: settings.accountType || 'student',
        consent: updatedConsent,
        parentEmail: settings.parentEmail || null
      });
      
      await saveConsentSettings({ 
        userId: user.uid, 
        accountType: settings.accountType || 'student', 
        consent: updatedConsent, 
        parentEmail: settings.parentEmail || null 
      });
      
      console.log('✅ Consent settings saved successfully');
      
      // Update settings state with saved values
      setSettings(prev => ({ ...(prev || {}), consent: updatedConsent }));
      setPendingChanges({}); // Clear pending changes
      setSaveSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error saving consent settings:', error);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const onChildRequestChange = async (key, desiredValue) => {
    if (!settings?.parentEmail) return;
    await notifyParentOnChildToggle({ parentEmail: settings.parentEmail, childUserId: user?.uid, toggleName: key, desiredValue });
    alert('Request sent to your parent for approval.');
  };

  if (loading) {
    return <div className={className}><div className="text-amber-800">Loading consent settings…</div></div>;
  }

  return (
    <div className={`bg-white rounded-2xl border-2 border-amber-200 p-4 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-bold text-amber-900">Account & Consent Settings</h3>
        {settings?.accountType && (
          <span className="text-sm text-amber-700">Type: {settings.accountType === 'ngo_admin' ? 'Art Mentor' : settings.accountType.charAt(0).toUpperCase()+settings.accountType.slice(1)}</span>
        )}
      </div>
      {error && <div className="text-red-600 text-sm mb-2">{error}</div>}

      {/* Defaults are auto-created when missing */}

      {settings && (
        <div>
          {isUnder13 && (
            <div className="text-sm text-amber-700 mb-3">These settings are managed by a parent/guardian. Changes by the child will send an approval request.</div>
          )}

          <div className="divide-y divide-amber-100">
            {UNDER_13_CONSENT_QUESTIONS.map(q => {
              // Use pending changes if they exist, otherwise use saved consent
              const val = pendingChanges.hasOwnProperty(q.id) ? !!pendingChanges[q.id] : !!consent[q.id];
              const label = isUnder13
                ? q.label
                : q.label.replace("my child's", 'my').replace("my child", 'my');
              const note = isUnder13 ? q.note : q.note;
              return (
                <div key={q.id} className="py-3 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium text-amber-900">{label}</div>
                    {note && <div className="text-sm text-amber-700">{note}</div>}
                  </div>
                  {/* Parent can edit directly; Child sees Request Change */}
                  {isUnder13 ? (
                    settings?.parentId === user?.uid || settings?.accountType === 'parent' ? (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={val} onChange={(e)=>onParentToggle(q.id, e.target.checked)} />
                        <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-amber-500 transition-colors"></div>
                        <span className="ml-3 text-sm text-amber-800">{val ? 'On' : 'Off'}</span>
                      </label>
                    ) : (
                      <button className="px-3 py-1 rounded bg-amber-500 text-white text-sm" onClick={()=>onChildRequestChange(q.id, !val)}>Request change ({val ? 'Turn Off' : 'Turn On'})</button>
                    )
                  ) : (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" checked={val} onChange={(e)=>onParentToggle(q.id, e.target.checked)} />
                      <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-amber-500 transition-colors"></div>
                      <span className="ml-3 text-sm text-amber-800">{val ? 'On' : 'Off'}</span>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Save Settings Button */}
          <div className="mt-6 pt-4 border-t border-amber-200">
            {Object.keys(pendingChanges).length > 0 && (
              <div className="mb-3 text-sm text-amber-700">
                You have unsaved changes. Click "Save Settings" to apply them.
              </div>
            )}
            {saveSuccess && (
              <div className="mb-3 text-sm text-green-600 font-medium">
                ✅ Settings saved successfully!
              </div>
            )}
            <button
              onClick={handleSaveSettings}
              disabled={saving || Object.keys(pendingChanges).length === 0}
              className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                saving || Object.keys(pendingChanges).length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md hover:shadow-lg'
              }`}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


